import { useEffect, useState } from 'react';
import { TagCategoryWithTags } from '../types';
import { tagService } from '../services/tagService';

/**
 * Tag categories for a tenant, cached at module level.
 *
 * The grouped catalogue/Home/Progress views resolve their layout from these
 * categories, and fetching them per page-mount made every navigation render
 * the flat (wrong) view first and swap once the response landed. The cache
 * makes the first resolution the only async one: pages show their loading
 * skeleton for that one fetch (never the wrong view), and every later
 * navigation renders the grouped view synchronously.
 *
 * Staleness is accepted: tags change rarely (admin tag editing), and a page
 * reload refreshes the cache — same trade-off as the Hub's trial-state cache.
 */
const cache = new Map<string, TagCategoryWithTags[]>();
const inflight = new Map<string, Promise<TagCategoryWithTags[]>>();

export function useTagCategories(
  tenantId: string | null,
  enabled: boolean,
): { categories: TagCategoryWithTags[]; isLoading: boolean } {
  const [categories, setCategories] = useState<TagCategoryWithTags[]>(
    () => (tenantId && cache.get(tenantId)) || [],
  );
  const [isLoading, setIsLoading] = useState(
    () => enabled && !!tenantId && !cache.has(tenantId),
  );

  useEffect(() => {
    if (!tenantId || !enabled) return;
    const cached = cache.get(tenantId);
    if (cached) {
      setCategories(cached);
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    let pending = inflight.get(tenantId);
    if (!pending) {
      pending = tagService
        .getCategoriesWithTags(tenantId)
        .finally(() => inflight.delete(tenantId));
      inflight.set(tenantId, pending);
    }
    pending
      .then((cats) => {
        cache.set(tenantId, cats);
        if (active) {
          setCategories(cats);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        // Degrades to the flat view — same rule as a broken grouping config.
        if (active) {
          console.error('Failed to load tag categories', err);
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [tenantId, enabled]);

  return { categories, isLoading };
}
